import API from '../../../../api/baseUrl';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../../store';
import { ViewMode } from './types';
import { getTokens } from '../../../../auth/AuthState';
import usePdfUtils from './usePdfUtils';

type PDFOptions = {
  isViewTime: boolean;
  isViewIcon: boolean;
  isDrLetterIncluded: boolean;
  isPharmLetterIncluded: boolean;
  isLargeFontSize: boolean;
  isSubheadingIncluded: boolean;
  extraRows: number;
};
export interface GetMedicalProfilePdfPayload {
  access_token: string;
  tenancyId: number;
  medProfileId: string;
  options: PDFOptions;
}

type PostGetPatientMedProfilePdfParams = {
  payload: Pick<GetMedicalProfilePdfPayload, 'medProfileId' | 'access_token'>;
  pdfOptions: {
    viewMode: ViewMode;
  };
  options: PDFOptions;
};
export const useMedicalProfilePdf = () => {
  const [errorMessage, setErrorMessage] = useState('');
  const { openPdfInNewTab, downloadPdfFromBase64 } = usePdfUtils();
  const [isLoading, setIsLoading] = useState<'download' | 'preview' | null>(
    null,
  );
  const { selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );
  const postGetPatientMedProfilePdf = async (
    type: 'preview' | 'download',
    {
      payload,
      pdfOptions,
      options: apiOptions,
    }: PostGetPatientMedProfilePdfParams,
  ) => {
    if (!selectedTenancy) return;
    setIsLoading(type);
    const { medProfileId, access_token } = payload;
    try {
      const { id: authorization_token } = await getTokens();
      let options = {
        headers: {
          Accept: '*/*',
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + authorization_token,
        },
      };
      const payload: GetMedicalProfilePdfPayload = {
        tenancyId: selectedTenancy.tenancy_id,
        medProfileId,
        access_token,
        options: apiOptions,
      };
      const { data } = await API.post(
        `getPatientMedProfilePdf`,
        { ...payload },
        options,
      );
      if (pdfOptions.viewMode === ViewMode.PREVIEW) {
        openPdfInNewTab(data);
      } else if (pdfOptions.viewMode === ViewMode.DOWNLOAD) {
        downloadPdfFromBase64(data);
      }
    } catch (error: any) {
      console.error('An error occurred during getPatientMedProfilePdf', error);
      setErrorMessage(
        error?.response?.data?.message ||
          'Unknown error in getPatientMedProfilePdf',
      );
    } finally {
      setIsLoading(null);
    }
  };

  return {
    postGetPatientMedProfilePdf,
    errorMessage,
    isLoading,
  };
};
